import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { MapDef } from "@rietz/shared";
import { makeLabel, type Label } from "./labels.js";
import {
  BOUNDS_COLOR,
  BOUNDS_OPACITY,
  EXTRACT_LABEL_COLOR,
  SPAWN_COLOR,
  SPAWN_OPACITY,
} from "./palette.js";

/**
 * F5 — the points a map author places by hand: spawn points, map bounds and
 * extraction volumes, none of which should be visible to a player. Three draw
 * calls plus one sprite per extract.
 */

export interface MarkerViz {
  group: THREE.Group;
  spawnCount: number;
  /** Extract name sprites actually built (0 without a DOM). */
  labelCount: number;
  dispose(): void;
}

/** Arrow proportions, metres. */
const ARROW_RADIUS = 0.32;
const ARROW_HEIGHT = 1.1;
/** Gap between the ground and the arrow's base, so it reads as hovering. */
const ARROW_LIFT = 0.15;

export function buildMarkerViz(map: MapDef): MarkerViz {
  const group = new THREE.Group();
  group.name = `debug:markers:${map.id}`;

  const disposers: (() => void)[] = [];

  // --- Spawn arrows: one merged mesh rather than eight draw calls.
  const cones: THREE.BufferGeometry[] = [];
  for (const sp of map.playerSpawns) {
    const cone = new THREE.ConeGeometry(ARROW_RADIUS, ARROW_HEIGHT, 6);
    cone.translate(sp.x, sp.y + ARROW_LIFT + ARROW_HEIGHT * 0.5, sp.z);
    cones.push(cone);
  }

  let spawnCount = 0;
  if (cones.length > 0) {
    const merged = mergeGeometries(cones, false);
    for (const c of cones) c.dispose();
    if (merged === null) throw new Error("buildMarkerViz: spawn merge failed");
    merged.computeBoundingSphere();

    const mat = new THREE.MeshBasicMaterial({
      color: SPAWN_COLOR,
      transparent: true,
      opacity: SPAWN_OPACITY,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(merged, mat);
    mesh.name = "spawns";
    mesh.frustumCulled = false;
    group.add(mesh);
    spawnCount = map.playerSpawns.length;
    disposers.push(() => {
      group.remove(mesh);
      merged.dispose();
      mat.dispose();
    });
  }

  // --- Map bounds ----------------------------------------------------------
  const b = map.bounds;
  const boundsBox = new THREE.BoxGeometry(
    b.max.x - b.min.x,
    b.max.y - b.min.y,
    b.max.z - b.min.z,
  );
  const boundsEdges = new THREE.EdgesGeometry(boundsBox);
  boundsBox.dispose();
  boundsEdges.translate(
    (b.min.x + b.max.x) * 0.5,
    (b.min.y + b.max.y) * 0.5,
    (b.min.z + b.max.z) * 0.5,
  );
  const boundsMat = new THREE.LineBasicMaterial({
    color: BOUNDS_COLOR,
    transparent: true,
    opacity: BOUNDS_OPACITY,
    depthWrite: false,
    // The bounds box sits beyond the fog's useful range on a big map; without
    // this it dissolves into the haze and tells you nothing.
    fog: false,
  });
  const boundsLines = new THREE.LineSegments(boundsEdges, boundsMat);
  boundsLines.name = "bounds";
  boundsLines.frustumCulled = false;
  group.add(boundsLines);
  disposers.push(() => {
    group.remove(boundsLines);
    boundsEdges.dispose();
    boundsMat.dispose();
  });

  // --- Extract name tags: the volumes are already drawn in-game; only the name
  // is missing.
  const labels: Label[] = [];
  for (const ex of map.extracts) {
    const label = makeLabel(ex.name, {
      color: EXTRACT_LABEL_COLOR,
      heightM: 0.85,
      fontPx: 44,
    });
    if (label === null) continue;
    label.sprite.position.set(
      (ex.zone.min.x + ex.zone.max.x) * 0.5,
      ex.zone.max.y + 0.85,
      (ex.zone.min.z + ex.zone.max.z) * 0.5,
    );
    group.add(label.sprite);
    labels.push(label);
  }

  return {
    group,
    spawnCount,
    labelCount: labels.length,
    dispose(): void {
      for (const l of labels) l.dispose();
      labels.length = 0;
      for (const d of disposers) d();
      disposers.length = 0;
    },
  };
}
